/**
 * AdminSigningTab — admin overview of all written statement signing versions.
 *
 * Shows a table of all versions with status/progress and per-version actions:
 * - Resend pending links
 * - Invalidate / cancel signing
 * - View audit detail dialog
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, CardContent, CardHeader, CardTitle,
} from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import {
  CheckCircle2, Clock, XCircle, Send, ShieldOff,
  Loader2, Search, RefreshCw, Info, FileText, Download,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  sent: <Badge className="bg-blue-100 text-blue-700 border-blue-200">Sent</Badge>,
  partially_signed: <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Partial</Badge>,
  fully_signed: <Badge className="bg-green-100 text-green-700 border-green-200">Signed</Badge>,
  superseded: <Badge className="bg-gray-100 text-gray-600 border-gray-200">Superseded</Badge>,
  draft: <Badge variant="outline">Draft</Badge>,
};

function fmtDate(raw) {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return raw;
  }
}

function AuditDialog({ open, onClose, versionId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !versionId) return;
    setLoading(true);
    axios
      .get(`${API_URL}/api/admin/statements/${versionId}/audit`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load audit data'))
      .finally(() => setLoading(false));
  }, [open, versionId]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Signing Audit Detail</DialogTitle>
          <DialogDescription>Full audit record for this signing version</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {data && !loading && (
          <div className="space-y-5 text-sm">
            {/* Version summary */}
            <div className="rounded-lg border p-4 space-y-2 bg-muted/30">
              <p className="font-semibold text-[#1e3a5f]">Version {data.version?.version_number}</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-muted-foreground">
                <span>Status</span>
                <span className="text-foreground font-medium capitalize">{data.version?.status}</span>
                <span>Sent at</span>
                <span>{fmtDate(data.version?.sent_at)}</span>
                <span>Completed at</span>
                <span>{fmtDate(data.version?.completed_at)}</span>
                {data.version?.pdf_checksum && (
                  <>
                    <span>PDF SHA-256</span>
                    <span className="font-mono text-xs break-all">{data.version.pdf_checksum}</span>
                  </>
                )}
                <span>Document</span>
                <span className="text-foreground">
                  {data.version?.final_pdf_path
                    ? <Badge className="text-xs bg-green-100 text-green-700 border-green-200">Merged final document</Badge>
                    : <Badge variant="outline" className="text-xs">Agreement only</Badge>}
                </span>
                <span>Certificate</span>
                <span className="text-foreground">
                  {data.version?.certificate_path
                    ? <Badge className="text-xs bg-blue-50 text-blue-700 border-blue-200">Included</Badge>
                    : <span className="text-muted-foreground">Not available</span>}
                </span>
                <span>IP handling</span>
                <span className="text-foreground text-xs">Masked (privacy-safe)</span>
              </div>
            </div>

            {/* Participants / signatures */}
            <div>
              <p className="font-semibold text-[#1e3a5f] mb-2">Signatories</p>
              <div className="space-y-2">
                {(data.version?.participants || []).map((p, idx) => {
                  const sig = (data.signatures || []).find((s) => s.participant_index === idx);
                  return (
                    <div key={idx} className="rounded border p-3 flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        {p.signed
                          ? <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                          : <Clock className="h-4 w-4 text-yellow-500 shrink-0" />}
                        <span className="font-medium">{p.signer_name}</span>
                        <Badge variant="outline" className="text-xs capitalize">{p.role}</Badge>
                        {!p.required && <Badge variant="outline" className="text-xs">Optional</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground">{p.email}</p>
                      {sig && (
                        <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-muted-foreground border-t pt-1">
                          <span>Signed at</span><span>{fmtDate(sig.signed_at)}</span>
                          <span>IP address</span><span className="font-mono">{sig.ip_address || '—'}</span>
                          <span>User agent</span><span className="truncate max-w-[200px]" title={sig.user_agent}>{sig.user_agent || '—'}</span>
                          <span>Audit ref</span><span className="font-mono">{sig.audit_ref || '—'}</span>
                          {sig.document_checksum && (
                            <><span>Doc checksum</span><span className="font-mono break-all">{sig.document_checksum}</span></>
                          )}
                        </div>
                      )}
                      {!sig && p.signed && (
                        <p className="text-xs text-yellow-600">Signed (no signature record found)</p>
                      )}
                      {!sig && !p.signed && (
                        <p className="text-xs text-muted-foreground">Awaiting signature</p>
                      )}
                      {p.reminder_count > 0 && (
                        <p className="text-xs text-muted-foreground">
                          Reminders sent: {p.reminder_count} · Last: {fmtDate(p.last_reminder_sent_at)}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Token records */}
            {(data.tokens || []).length > 0 && (
              <div>
                <p className="font-semibold text-[#1e3a5f] mb-2">Token Records</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Participant</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Used</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.tokens.map((t, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs">{t.participant_index}</TableCell>
                        <TableCell className="text-xs">{fmtDate(t.expires_at)}</TableCell>
                        <TableCell>
                          {t.used
                            ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                            : <Clock className="h-3.5 w-3.5 text-yellow-500" />}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AdminSigningTab({ versions = [], loading = false, onRefresh }) {
  const [search, setSearch] = useState('');
  const [auditVersionId, setAuditVersionId] = useState(null);
  const [confirm, setConfirm] = useState({ open: false, title: '', description: '', onConfirm: null });
  const [actionLoading, setActionLoading] = useState(null); // version_id of in-flight action
  const [downloadingId, setDownloadingId] = useState(null); // statement_id being downloaded

  async function downloadFinal(statementId) {
    if (downloadingId) return;
    setDownloadingId(statementId);
    try {
      const response = await axios.get(
        `${API_URL}/api/written-statements/${statementId}/download`,
        { withCredentials: true, responseType: 'blob' },
      );
      const disposition = response.headers['content-disposition'] || '';
      const nameMatch = disposition.match(/filename="?([^"]+)"?/);
      const filename = nameMatch ? nameMatch[1] : `signed_statement_${statementId}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download document');
    } finally {
      setDownloadingId(null);
    }
  }

  const filtered = versions.filter((v) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      v.property_address?.toLowerCase().includes(q) ||
      v.status?.toLowerCase().includes(q) ||
      v.participants?.some((p) => p.signer_name?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q))
    );
  });

  async function doAction(versionId, endpoint) {
    setActionLoading(versionId + endpoint);
    try {
      await axios.post(`${API_URL}/api/admin/statements/${versionId}/${endpoint}`, {}, { withCredentials: true });
      toast.success('Action completed');
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  }

  function confirmAction(title, description, fn) {
    setConfirm({ open: true, title, description, onConfirm: fn });
  }

  const isActive = (v) => ['sent', 'partially_signed'].includes(v.status);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-[#1e3a5f]" />
              Documents &amp; Signing
            </CardTitle>
            <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by address, name or status…"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </CardHeader>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Property</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead>Completed</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-10">
                    No signing versions found
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.map((v) => (
                <TableRow key={v.id} className="align-top">
                  <TableCell className="max-w-[200px]">
                    <p className="font-medium text-sm truncate" title={v.property_address}>
                      {v.property_address || '—'}
                    </p>
                    <p className="text-xs text-muted-foreground">v{v.version_number}</p>
                  </TableCell>
                  <TableCell>
                    {STATUS_BADGE[v.status] ?? <Badge variant="outline">{v.status}</Badge>}
                    {v.admin_override && (
                      <Badge variant="outline" className="text-xs ml-1 border-orange-300 text-orange-600">Override</Badge>
                    )}
                    {v.final_pdf_path && (
                      <Badge className="text-xs ml-1 mt-1 block w-fit bg-green-100 text-green-700 border-green-200">
                        Final document ready
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-sm">
                      {v.participants?.filter((p) => p.required && p.signed).length ?? 0}
                      <span className="text-muted-foreground">/</span>
                      {v.participants?.filter((p) => p.required).length ?? 0}
                      <span className="text-xs text-muted-foreground">signed</span>
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {(v.participants || []).slice(0, 3).map((p, i) => (
                        <div key={i} className="flex items-center gap-1 text-xs text-muted-foreground">
                          {p.signed
                            ? <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0" />
                            : <Clock className="h-3 w-3 text-yellow-400 shrink-0" />}
                          <span className="truncate max-w-[120px]">{p.signer_name}</span>
                          <span className="capitalize text-gray-400">({p.role})</span>
                        </div>
                      ))}
                      {(v.participants || []).length > 3 && (
                        <p className="text-xs text-muted-foreground">+{v.participants.length - 3} more</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {fmtDate(v.sent_at)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {fmtDate(v.completed_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-col items-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setAuditVersionId(v.id)}
                      >
                        <Info className="h-3.5 w-3.5 mr-1" />
                        Audit
                      </Button>

                      {v.final_pdf_path && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-[#1e3a5f] hover:bg-blue-50"
                          disabled={downloadingId === v.statement_id}
                          title="Download final document"
                          onClick={() => downloadFinal(v.statement_id)}
                        >
                          {downloadingId === v.statement_id
                            ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            : <Download className="h-3.5 w-3.5 mr-1" />}
                          Download
                        </Button>
                      )}

                      {isActive(v) && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-blue-700 hover:bg-blue-50"
                            disabled={!!actionLoading}
                            onClick={() =>
                              confirmAction(
                                'Reissue signing links?',
                                'Previous signing links will be invalidated and replaced. New invite emails will be sent to all outstanding signers.',
                                () => doAction(v.id, 'resend'),
                              )
                            }
                          >
                            {actionLoading === v.id + 'resend'
                              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              : <Send className="h-3.5 w-3.5 mr-1" />}
                            Reissue links
                          </Button>

                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-red-600 hover:bg-red-50"
                            disabled={!!actionLoading}
                            onClick={() =>
                              confirmAction(
                                'Invalidate signing?',
                                'All unused tokens will be cancelled and this version will be marked superseded. This cannot be undone.',
                                () => doAction(v.id, 'invalidate'),
                              )
                            }
                          >
                            {actionLoading === v.id + 'invalidate'
                              ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              : <ShieldOff className="h-3.5 w-3.5 mr-1" />}
                            Invalidate
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {!loading && (
          <CardContent className="pt-3 pb-3">
            <p className="text-xs text-muted-foreground">{filtered.length} version(s)</p>
          </CardContent>
        )}
      </Card>

      {/* Audit detail dialog */}
      <AuditDialog
        open={!!auditVersionId}
        onClose={() => setAuditVersionId(null)}
        versionId={auditVersionId}
      />

      {/* Confirmation dialog */}
      <AlertDialog open={confirm.open} onOpenChange={(o) => !o && setConfirm((p) => ({ ...p, open: false }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex justify-end gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (confirm.onConfirm) await confirm.onConfirm();
                setConfirm((p) => ({ ...p, open: false, onConfirm: null }));
              }}
            >
              Confirm
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
